import { EnvironmentProviders, computed, inject, provideAppInitializer } from '@angular/core';

import { TfsIconName } from '@tfs/material/tfs-icon';
import { DataSyncJob, DataSyncSource, provideDataSyncSource } from '@tfs/shared-feature-data-sync';

import { ToolImportStatus } from '../models/data-import.model';
import { ToolImportJobStatusChangePayload } from '../models/tool-import-events.model';
import { ToolImportNotifications } from '../tool-import-notifications';

const TOOL_IMPORT_SOURCE_ID = 'tool-import';
const TOOL_IMPORT_SOURCE_NAME = 'Tool data import';

export function provideToolImportDataSync(): EnvironmentProviders[] {
  return [
    provideAppInitializer(() => {
      inject(ToolImportNotifications);
    }),
    provideDataSyncSource(() => {
      const toolImportNotifications = inject(ToolImportNotifications);
      return {
        id: TOOL_IMPORT_SOURCE_ID,
        jobs: computed(() => toolImportNotifications.toolImports().map(toDataSyncJob)),
        dismiss: toolImportId => toolImportNotifications.dismissImport(toolImportId),
      } satisfies DataSyncSource;
    }),
  ];
}

function toDataSyncJob(toolImport: ToolImportJobStatusChangePayload): DataSyncJob {
  const state = mapToolImportState(toolImport.status);
  return {
    id: toolImport.toolImportId,
    sourceId: TOOL_IMPORT_SOURCE_ID,
    state,
    label: mapToolImportLabel(state),
    icon: mapToolImportIcon(state),
  };
}

function mapToolImportState(status: ToolImportStatus): DataSyncJob['state'] {
  if (status === ToolImportStatus.Failed) return 'error';
  if (status === ToolImportStatus.Completed) return 'success';
  return 'active';
}

function mapToolImportLabel(state: DataSyncJob['state']): string {
  switch (state) {
    case 'success':
      return `${TOOL_IMPORT_SOURCE_NAME} complete`;
    case 'error':
      return `${TOOL_IMPORT_SOURCE_NAME} failed`;
    default:
      return TOOL_IMPORT_SOURCE_NAME;
  }
}

function mapToolImportIcon(state: DataSyncJob['state']): TfsIconName {
  switch (state) {
    case 'success':
      return 'check-circle-2';
    case 'error':
      return 'alert-circle';
    default:
      return 'cloud-upload';
  }
}
